import React from 'react';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { useStyles } from './styles';
import 'react-circular-progressbar/dist/styles.css';
import { IFriend } from '../../../../../store/models';
import { useLocale } from '../../../../../providers/LocaleProvider';
import { Dialog, useMediaQuery } from '@mui/material';
import closeIcon from "src/assets/icons/x.svg";
import clockIcon from "src/assets/icons/ClockCounterClockwise.svg";

interface FriendHistoryModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    friend: IFriend;
}

export const FriendHistoryModal: React.FC<FriendHistoryModalProps> = ({
    isOpen,
    onOpenChange,
    friend
}) => {
    const classes = useStyles();

    const handleClose = () => {
        onOpenChange(false);
    };

    const {
        profile: {
            statistics: {
                classic,
                gamesPlayed,
                wins,
                losses,
                totalG,
                totalW,
                totalL,
                maxWinPot,
                maxLosePot,
                winStr,
                loseStr,
                score,
            },
        },
    } = useLocale();

    const isMobile = useMediaQuery("(max-width: 560px)");
    const isTablet = useMediaQuery("(max-width: 1024px)");

    // Статистика для классических шахмат
    const classicStats = {
        wins: friend?.wins?.[1] || 0,
        losses: friend?.losses?.[1] || 0,
        total: (friend?.wins?.[1] || 0) + (friend?.losses?.[1] || 0),
        winRate: 0
    };
    classicStats.winRate = classicStats.total > 0
        ? Math.round((classicStats.wins / classicStats.total) * 100)
        : 50;

    // Статистика для шахмат 960
    const chess960Stats = {
        wins: friend?.wins?.[2] || 0,
        losses: friend?.losses?.[2] || 0,
        total: (friend?.wins?.[2] || 0) + (friend?.losses?.[2] || 0),
        winRate: 0
    };
    chess960Stats.winRate = chess960Stats.total > 0
        ? Math.round((chess960Stats.wins / chess960Stats.total) * 100)
        : 50;

    // Общая статистика
    const totalGamesCount = classicStats.total + chess960Stats.total;
    const totalWinsCount = classicStats.wins + chess960Stats.wins;
    const totalLossesCount = classicStats.losses + chess960Stats.losses;
    const winStreakCount = friend?.winStreak || 0; // Меняем местами loseStreak и winStreak
    const loseStreakCount = friend?.losesStreak || 0;

    return (
        <Dialog
            open={isOpen}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            disableEscapeKeyDown
            maxWidth={false}
            className="modal"
        >
            <div className={classes.modalContent}>
                <div className={classes.header}>
                    <div className={classes.title}>
                        <img src={clockIcon} alt="clock" />
                        {score("Score with")} <span>{friend?.user?.nickname}</span>
                    </div>
                    <button className={classes.closeButton} onClick={handleClose}>
                        <img src={closeIcon} alt="close" />
                    </button>
                </div>

                <div className={classes.contentWrapper}>
                    <div className={classes.gameModeGrid}>
                        {/* Classic */}
                        <div className={classes.gameModeCard}>
                            <div className={classes.cardHeader}>
                                <div className={classes.cardTitle}>{classic("Classic")}</div>
                                <div className={classes.gamesPlayed}>
                                    {gamesPlayed("Games Played")}: <span>{classicStats.total}</span>
                                </div>
                            </div>
                            <div className={classes.separator}></div>
                            <div className={classes.statsBlock}>
                                <div className={classes.circularProgressWrapper}>
                                    <CircularProgressbarWithChildren
                                        value={classicStats.winRate}
                                        strokeWidth={14}
                                        styles={buildStyles({
                                            rotation: 0,
                                            strokeLinecap: 'butt',
                                            pathColor: "#00BA6C",
                                            trailColor: "#FF1C29",
                                            pathTransitionDuration: 0.5
                                        })}
                                    >
                                        <div className={classes.progressStats}>
                                            <div className={classes.winsText}>
                                                {classicStats.wins} • <span>{classicStats.winRate}%</span>
                                            </div>
                                            <div className={classes.lossesText}>
                                                {classicStats.losses} • <span>{100 - classicStats.winRate}%</span>
                                            </div>
                                        </div>
                                    </CircularProgressbarWithChildren>
                                </div>
                                <div className={classes.labelsContainer}>
                                    <div className={classes.labelItem}>
                                        <span className={classes.placeholderCircle} style={{ backgroundColor: '#4CAF50' }}></span>
                                        {wins("Wins")}
                                    </div>
                                    <div className={classes.labelItem}>
                                        <span className={classes.placeholderCircle} style={{ backgroundColor: '#F44336' }}></span>
                                        {losses("Losses")}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 960 */}
                        <div className={classes.gameModeCard}>
                            <div className={classes.cardHeader}>
                                <div className={classes.cardTitle}>960</div>
                                <div className={classes.gamesPlayed}>
                                    {gamesPlayed("Games Played")}: <span>{chess960Stats.total}</span>
                                </div>
                            </div>
                            <div className={classes.separator}></div>
                            <div className={classes.statsBlock}>
                                <div className={classes.circularProgressWrapper}>
                                    <CircularProgressbarWithChildren
                                        value={chess960Stats.winRate}
                                        strokeWidth={14}
                                        styles={buildStyles({
                                            rotation: 0,
                                            strokeLinecap: 'butt',
                                            pathColor: "#00BA6C",
                                            trailColor: "#FF1C29",
                                            pathTransitionDuration: 0.5
                                        })}
                                    >
                                        <div className={classes.progressStats}>
                                            <div className={classes.winsText}>
                                                {chess960Stats.wins} • <span>{chess960Stats.winRate}%</span>
                                            </div>
                                            <div className={classes.lossesText}>
                                                {chess960Stats.losses} • <span>{100 - chess960Stats.winRate}%</span>
                                            </div>
                                        </div>
                                    </CircularProgressbarWithChildren>
                                </div>
                                <div className={classes.labelsContainer}>
                                    <div className={classes.labelItem}>
                                        <span className={classes.placeholderCircle}></span>
                                        {wins("Wins")}
                                    </div>
                                    <div className={classes.labelItem}>
                                        <span className={classes.placeholderCircleLoss}></span>
                                        {losses("Losses")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className={classes.statsGrid}>
                        {isMobile ? (
                            <>
                                <div className={classes.totalGamesCard}>
                                    <div className={classes.statsLabel}>{totalG("Total games")}</div>
                                    <div className={classes.statsValue}>{totalGamesCount}</div>
                                </div>

                                <div className={classes.mobileStatsGroup}>
                                    <div className={classes.statsPairRow}>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{totalW("Total wins")}</div>
                                            <div className={classes.statsValueGreen}>{totalWinsCount}</div>
                                        </div>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{totalL("Total losses")}</div>
                                            <div className={classes.statsValueRed}>{totalLossesCount}</div>
                                        </div>
                                    </div>

                                    <div className={classes.statsPairRow}>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{maxWinPot("Max. win pot")}</div>
                                            <div className={classes.statsValueGreen}>$ {friend?.maxPotWin || 0}</div>
                                        </div>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{maxLosePot("Max. lose pot")}</div>
                                            <div className={classes.statsValueRed}>$ {friend?.maxPotLose || 0}</div>
                                        </div>
                                    </div>

                                    <div className={classes.statsPairRow}>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{winStr("Win streak")}</div>
                                            <div className={classes.statsValueGreen}>{winStreakCount}</div>
                                        </div>
                                        <div className={classes.statsCard}>
                                            <div className={classes.statsLabel}>{loseStr("Lose streak")}</div>
                                            <div className={classes.statsValueRed}>{loseStreakCount}</div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={classes.statsRow}>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{totalG("Total games")}</div>
                                        <div className={classes.statsValue}>{totalGamesCount}</div>
                                    </div>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{totalW("Total wins")}</div>
                                        <div className={classes.statsValueGreen}>{totalWinsCount}</div>
                                    </div>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{totalL("Total losses")}</div>
                                        <div className={classes.statsValueRed}>{totalLossesCount}</div>
                                    </div>
                                </div>
                                <div className={classes.statsRow}>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{maxWinPot("Max. win pot")}</div>
                                        <div className={classes.statsValueGreen}>$ {friend?.maxPotWin || 0}</div>
                                    </div>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{maxLosePot("Max. lose pot")}</div>
                                        <div className={classes.statsValueRed}>$ {friend?.maxPotLose || 0}</div>
                                    </div>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{winStr("Win streak")}</div>
                                        <div className={classes.statsValueGreen}>{winStreakCount}</div>
                                    </div>
                                    <div className={classes.statsCard}>
                                        <div className={classes.statsLabel}>{loseStr("Lose streak")}</div>
                                        <div className={classes.statsValueRed}>{loseStreakCount}</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default FriendHistoryModal;
